import React from 'react';
import { Box, Flex, SimpleGrid, Spinner, Text, Tooltip } from '@chakra-ui/react';
import {
  Archive,
  Buildings,
  DownloadSimple,
  FileArrowDown,
  Calculator,
  CalendarX,
  FileX,
  CheckCircle,
  Clock,
  Hourglass,
  IdentificationCard,
  Lock,
  PaperPlaneTilt,
  Receipt,
  Scales,
  Checks,
  Warning,
  XCircle,
  CircleDashed,
} from 'phosphor-react';
import { Etapa, ITotais } from './api';
import { brl } from './regras';

interface IIndicador {
  filtro: string;
  rotulo: string;
  cor: string;
  icone: React.ReactNode;
  dica: string;
  valor: (t?: ITotais) => number;
  /** linha de apoio (ex.: valor do DAS da etapa) */
  sub?: (t?: ITotais) => string | null;
}

const etapa = (t: ITotais | undefined, e: Etapa) => t?.etapas?.[e] ?? 0;

/** O caminho do mês, na ordem: cada empresa está em UMA etapa. */
export const ETAPAS: IIndicador[] = [
  {
    filtro: 'nao_calculada',
    rotulo: 'Não calculadas',
    cor: '#94A3B8',
    icone: <Hourglass size={18} weight="duotone" />,
    dica: 'Sem cálculo neste mês: clique em Calcular ou Ações → Recalcular.',
    valor: t => t?.etapas?.nao_calculada ?? t?.nao_calculadas ?? 0,
  },
  {
    filtro: 'bloqueio',
    rotulo: 'Com bloqueio',
    cor: '#DC2626',
    icone: <Lock size={18} weight="duotone" />,
    dica: 'Bloqueio não se aceita: precisa corrigir a causa (cadastro, nota, RBT12, procuração) e recalcular.',
    valor: t => etapa(t, 'bloqueio'),
  },
  {
    filtro: 'atencao',
    rotulo: 'Só atenção',
    cor: '#F59E0B',
    icone: <Warning size={18} weight="duotone" />,
    dica: 'Sem bloqueio, mas com pendência de atenção: aceite (justificativa opcional) ou corrija.',
    valor: t => etapa(t, 'atencao'),
  },
  {
    filtro: 'pronta_aprovar',
    rotulo: 'Prontas p/ aprovar',
    cor: '#0EA5E9',
    icone: <Checks size={18} weight="duotone" />,
    dica: 'Calculadas e sem nenhuma pendência aberta: só falta aprovar.',
    valor: t => etapa(t, 'pronta_aprovar'),
  },
  {
    filtro: 'aprovada',
    rotulo: 'Prontas p/ transmitir',
    cor: '#6366F1',
    icone: <CheckCircle size={18} weight="duotone" />,
    dica: 'Aprovadas: entram no lote "Transmitir aprovadas".',
    valor: t => etapa(t, 'aprovada'),
    sub: t => (t?.das ? `DAS ${brl(t.das.a_transmitir)}` : null),
  },
  {
    filtro: 'transmitida',
    rotulo: 'Transmitidas',
    cor: '#14B8A6',
    icone: <PaperPlaneTilt size={18} weight="duotone" />,
    dica: 'Declaração entregue; faltam DAS e extrato (Ações → Gerar DAS e extrato).',
    valor: t => etapa(t, 'transmitida'),
  },
  {
    filtro: 'concluida',
    rotulo: 'Concluídas',
    cor: '#22C55E',
    icone: <Receipt size={18} weight="duotone" />,
    dica: 'Declaração, recibo, DAS e extrato salvos.',
    valor: t => etapa(t, 'concluida'),
  },
  {
    filtro: 'erro',
    rotulo: 'Com erro',
    cor: '#EF4444',
    icone: <XCircle size={18} weight="duotone" />,
    dica: 'O SERPRO recusou: veja a mensagem no card.',
    valor: t => etapa(t, 'erro'),
  },
];

/** Recortes que cruzam as etapas (uma empresa pode estar em vários). */
const RECORTES: IIndicador[] = [
  {
    filtro: 'divergentes',
    rotulo: 'Diferente do Domínio',
    cor: '#EF4444',
    icone: <Scales size={16} weight="duotone" />,
    dica: 'Nosso DAS difere mais de R$ 1 do Domínio (o Domínio é só referência).',
    valor: t => t?.divergentes_dominio ?? 0,
    sub: t =>
      t?.batem_dominio !== undefined
        ? `${t.batem_dominio} batem · ${t.sem_dominio ?? 0} sem Domínio`
        : null,
  },
  {
    filtro: 'sem_procuracao',
    rotulo: 'Sem procuração',
    cor: '#B45309',
    icone: <IdentificationCard size={16} weight="duotone" />,
    dica: 'O SERPRO recusou por falta de procuração no e-CAC para o escritório. Some quando a Receita responder.',
    valor: t => t?.sem_procuracao ?? 0,
  },
  {
    filtro: 'sem_movimento',
    rotulo: 'Sem movimento',
    cor: '#64748B',
    icone: <CircleDashed size={16} weight="duotone" />,
    dica: 'Receita zero no mês: a declaração sai sem DAS.',
    valor: t => t?.sem_movimento ?? 0,
  },
  {
    filtro: 'pendencias',
    rotulo: 'Com pendências',
    cor: '#F59E0B',
    icone: <Warning size={16} weight="duotone" />,
    dica: 'Qualquer pendência aberta (atenção ou bloqueio), em qualquer etapa.',
    valor: t => t?.com_pendencias ?? 0,
  },
  {
    filtro: 'sem_rbt12',
    rotulo: 'Sem RBT12',
    cor: '#DC2626',
    icone: <FileX size={16} weight="duotone" />,
    dica: 'Nem os 12 meses, nem início de atividade, nem declaração lida. Use Ações → Buscar RBT12 das faltantes. Mês sem faturamento não é falta.',
    valor: t => t?.sem_rbt12 ?? 0,
  },
  {
    filtro: 'com_fatura',
    rotulo: 'Com fatura',
    cor: '#0EA5E9',
    icone: <Receipt size={16} weight="duotone" />,
    dica: 'Fatura nos 3 meses anteriores (espécie 42 no Domínio ou tela de Faturas) ou já lançada neste mês. Mesma regra do Lucro Presumido.',
    valor: t => t?.com_fatura ?? 0,
  },
  {
    filtro: 'fatura_pendente',
    rotulo: 'Fatura não lançada',
    cor: '#EA580C',
    icone: <CalendarX size={16} weight="duotone" />,
    dica: 'Teve fatura nos 3 meses anteriores e nada lançado na tela de Faturas neste mês: a receita pode estar faltando.',
    valor: t => t?.fatura_pendente ?? 0,
  },
];

// depois de transmitir: gerar DAS/extrato -> baixar para a máquina -> baixados
const DOCUMENTOS: IIndicador[] = [
  {
    filtro: 'aguardando_documentos',
    rotulo: 'Aguardando DAS/extrato',
    cor: '#0EA5E9',
    icone: <FileArrowDown size={16} weight="duotone" />,
    dica: 'Transmitidas que ainda não geraram a guia e o extrato. Use Ações → Gerar DAS e extrato.',
    valor: t => t?.aguardando_documentos ?? 0,
  },
  {
    filtro: 'aguardando_baixa',
    rotulo: 'Aguardando baixa',
    cor: '#7C3AED',
    icone: <DownloadSimple size={16} weight="duotone" />,
    dica: 'DAS e extrato gerados e ainda não baixados para a máquina. Use Ações → Baixar documentos (ZIP).',
    valor: t => t?.aguardando_baixa ?? 0,
  },
  {
    filtro: 'docs_baixados',
    rotulo: 'Já baixados em lote',
    cor: '#15803D',
    icone: <Archive size={16} weight="duotone" />,
    dica: 'Documentos já baixados em ZIP pelo menos uma vez. Se o DAS mudar (retificação), a empresa volta para Aguardando baixa.',
    valor: t => t?.docs_baixados ?? 0,
  },
];

function Indicador({
  ind,
  totais,
  carregando,
  ativo,
  onClick,
  compacto,
}: {
  ind: IIndicador;
  totais?: ITotais;
  carregando: boolean;
  ativo: boolean;
  onClick: () => void;
  compacto?: boolean;
}) {
  const sub = ind.sub?.(totais);
  return (
    <Tooltip label={ind.dica} fontSize="11px" hasArrow placement="top" openDelay={300}>
      <Box
        as="button"
        type="button"
        onClick={onClick}
        aria-pressed={ativo}
        textAlign="left"
        w="full"
        p={compacto ? '8px 10px' : '10px 12px'}
        borderRadius="14px"
        border="1.5px solid"
        borderColor={ativo ? ind.cor : 'rgba(15,23,42,0.07)'}
        bg={ativo ? `${ind.cor}14` : 'rgba(255,255,255,0.65)'}
        transition="all 0.14s"
        _hover={{ borderColor: `${ind.cor}66`, transform: 'translateY(-1px)' }}
        _focusVisible={{ outline: `2px solid ${ind.cor}`, outlineOffset: '2px' }}
      >
        <Flex align="center" gap="10px">
          <Flex
            w={compacto ? '28px' : '32px'}
            h={compacto ? '28px' : '32px'}
            borderRadius="10px"
            align="center"
            justify="center"
            flex="none"
            bg={`${ind.cor}1A`}
            color={ind.cor}
          >
            {ind.icone}
          </Flex>
          <Box minW={0}>
            <Text fontSize="10.5px" color="#64748B" fontWeight={600} lineHeight="1.25">
              {ind.rotulo}
            </Text>
            {carregando ? (
              <Spinner size="xs" color={ind.cor} mt={1} />
            ) : (
              <Text
                fontSize={compacto ? '16px' : '18px'}
                fontWeight={800}
                color="#0F172A"
                lineHeight="1.2"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {ind.valor(totais).toLocaleString('pt-BR')}
              </Text>
            )}
            {sub && !carregando && (
              <Text fontSize="10.5px" color="#64748B" noOfLines={1}>
                {sub}
              </Text>
            )}
          </Box>
        </Flex>
      </Box>
    </Tooltip>
  );
}

/** Dias até o dia 20 do mês seguinte à competência (vencimento do PGDAS-D/DAS). */
export function prazo(competencia: string, hoje = new Date()): { data: Date; dias: number } {
  const [a, m] = competencia.split('-').map(Number);
  const data = new Date(a, m, 20); // mês 0-based: m = mês seguinte
  const zero = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return { data, dias: Math.round((data.getTime() - zero.getTime()) / 86400000) };
}

function Valor({
  rotulo,
  valor,
  cor,
  dica,
}: {
  rotulo: string;
  valor: string;
  cor?: string;
  dica: string;
}) {
  return (
    <Tooltip label={dica} fontSize="11px" hasArrow openDelay={300}>
      <Box minW="120px">
        <Text
          fontSize="10.5px"
          color="#94A3B8"
          fontWeight={700}
          textTransform="uppercase"
          letterSpacing="0.06em"
        >
          {rotulo}
        </Text>
        <Text
          fontSize="15px"
          fontWeight={800}
          color={cor ?? '#0F172A'}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {valor}
        </Text>
      </Box>
    </Tooltip>
  );
}

export function PainelKpis({
  totais,
  carregando,
  situacao,
  competencia,
  onFiltro,
}: {
  totais?: ITotais;
  carregando: boolean;
  situacao: string;
  competencia: string;
  onFiltro: (filtro: string) => void;
}) {
  const total = totais?.total_empresas ?? 0;
  const declaradas = totais?.declaradas ?? 0;
  const pct = Math.round((totais?.pct_declarado ?? 0) * 100);
  const { data: vence, dias } = prazo(competencia);
  const faltam = total - declaradas;
  let corPrazo = '#16A34A';
  if (faltam > 0 && dias < 0) corPrazo = '#DC2626';
  else if (faltam > 0 && dias <= 5) corPrazo = '#F59E0B';
  let textoPrazo = 'todas declaradas';
  if (faltam > 0) {
    if (dias > 1) textoPrazo = `vence em ${dias} dias`;
    else if (dias === 1) textoPrazo = 'vence amanhã';
    else if (dias === 0) textoPrazo = 'vence hoje';
    else textoPrazo = `venceu há ${-dias} dia(s)`;
  }
  const clicar = (f: string) => onFiltro(situacao === f ? '' : f);

  return (
    <Flex direction="column" gap={3}>
      {/* progresso do mês + valores */}
      <Flex gap={5} wrap="wrap" align="center">
        <Box flex="1 1 340px" minW={0}>
          <Flex align="baseline" gap={2} mb={1.5} wrap="wrap">
            <Text
              as="button"
              type="button"
              onClick={() => onFiltro('')}
              fontSize="10.5px"
              fontWeight={700}
              color={situacao ? '#1B88F5' : '#94A3B8'}
              letterSpacing="0.12em"
              textTransform="uppercase"
            >
              <Flex as="span" align="center" gap={1}>
                <Buildings size={13} weight="bold" /> {total.toLocaleString('pt-BR')} empresas
                {situacao ? ' · limpar filtro' : ''}
              </Flex>
            </Text>
            <Text fontSize="13px" color="#0F172A" fontWeight={700}>
              {declaradas} declarada(s) · {pct}%
            </Text>
            <Flex align="center" gap={1} color={corPrazo} fontSize="12px" fontWeight={700}>
              <Clock size={14} weight="bold" />
              {textoPrazo} ({vence.toLocaleDateString('pt-BR')})
            </Flex>
          </Flex>
          {/* barra segmentada: a mesma cor de cada etapa */}
          <Flex
            h="10px"
            borderRadius="full"
            overflow="hidden"
            bg="#E2E8F0"
            role="img"
            aria-label="Etapas do mês"
          >
            {total > 0 &&
              ETAPAS.map(e => {
                const v = e.valor(totais);
                if (!v) return null;
                return (
                  <Tooltip key={e.filtro} label={`${e.rotulo}: ${v}`} fontSize="11px" hasArrow>
                    <Box
                      as="button"
                      type="button"
                      onClick={() => clicar(e.filtro)}
                      w={`${(v / total) * 100}%`}
                      bg={e.cor}
                      opacity={situacao && situacao !== e.filtro ? 0.35 : 1}
                      transition="opacity 0.15s"
                    />
                  </Tooltip>
                );
              })}
          </Flex>
        </Box>
        <Flex gap={5} wrap="wrap">
          <Valor
            rotulo="Receita"
            valor={brl(totais?.receita)}
            dica="Receita bruta das empresas calculadas"
          />
          <Valor
            rotulo="Nosso DAS"
            valor={brl(totais?.valor_das)}
            dica="Soma do DAS calculado aqui"
          />
          <Valor
            rotulo="Domínio"
            valor={brl(totais?.valor_dominio)}
            dica="Soma do DAS do Domínio (das empresas que têm apuração lá): só referência"
          />
          <Valor
            rotulo="A transmitir"
            valor={brl(totais?.das?.a_transmitir)}
            cor="#6366F1"
            dica="DAS das aprovadas, prontas para o lote de transmissão"
          />
          <Valor
            rotulo="Declarado"
            valor={brl(totais?.das?.declarado)}
            cor="#0F766E"
            dica="DAS das transmitidas e concluídas"
          />
        </Flex>
      </Flex>

      <SimpleGrid columns={{ base: 2, md: 4, '2xl': 8 }} spacing={2}>
        {ETAPAS.map(e => (
          <Indicador
            key={e.filtro}
            ind={e}
            totais={totais}
            carregando={carregando}
            ativo={situacao === e.filtro}
            onClick={() => clicar(e.filtro)}
          />
        ))}
      </SimpleGrid>

      <Flex align="center" gap={2} wrap="wrap">
        <Text
          fontSize="10.5px"
          fontWeight={700}
          color="#94A3B8"
          letterSpacing="0.12em"
          textTransform="uppercase"
          mr={1}
        >
          <Flex as="span" align="center" gap={1}>
            <Calculator size={13} weight="bold" /> Recortes
          </Flex>
        </Text>
        <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={2} flex={1}>
          {RECORTES.map(r => (
            <Indicador
              key={r.filtro}
              ind={r}
              totais={totais}
              carregando={carregando}
              ativo={situacao === r.filtro}
              onClick={() => clicar(r.filtro)}
              compacto
            />
          ))}
        </SimpleGrid>
      </Flex>

      <Flex align="center" gap={2} wrap="wrap">
        <Text
          fontSize="10.5px"
          fontWeight={700}
          color="#94A3B8"
          letterSpacing="0.12em"
          textTransform="uppercase"
          mr={1}
        >
          <Flex as="span" align="center" gap={1}>
            <FileArrowDown size={13} weight="bold" /> Documentos
          </Flex>
        </Text>
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2} flex={1}>
          {DOCUMENTOS.map(r => (
            <Indicador
              key={r.filtro}
              ind={r}
              totais={totais}
              carregando={carregando}
              ativo={situacao === r.filtro}
              onClick={() => clicar(r.filtro)}
              compacto
            />
          ))}
        </SimpleGrid>
      </Flex>
    </Flex>
  );
}
